import React, {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useToast} from './Toast';
import {Project} from '../domain/types';
import {pickFiles, AUDIO_ACCEPT} from '../lib/browserFiles';
import {useAppDispatch} from '../store/hooks';
import {addToQueue} from '../store/slices/queueThunks';

interface AudioToCSVBatchContainerProps {
    projectId?: string;
    project?: Project | null;
}

const TranscribeBatchContainer: React.FC<AudioToCSVBatchContainerProps> = ({projectId}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {showToast} = useToast();
  // Real File objects, so the names shown are the names that will be uploaded.
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  const handleChooseFiles = async () => {
    const files = await pickFiles({accept: AUDIO_ACCEPT, multiple: true});

    if (files) {
      setSelectedFiles(files);
    }
  };

  const handleAddToQueue = async () => {
    if (selectedFiles.length === 0) {
      showToast('Please select audio files to add to the queue.', 'error');
      return;
    }

    try {
      for (const file of selectedFiles) {
        const baseName = file.name.replace(/\.[^/.]+$/, '');
        const csvFileName = `${baseName}-transcription.csv`;

        // Rejects until the server-side queue exists — see queueThunks.
        await dispatch(addToQueue({
          audioPath: file.name,
          csvFileName,
          projectId: projectId || null,
        })).unwrap();
      }

      showToast(`Added ${selectedFiles.length} file(s) to the queue!`, 'success');
      setSelectedFiles([]);

            // Navigate to queue page
      if (projectId) {
        navigate(`/projects/${projectId}/queue`);
      } else {
        navigate('/queue');
      }
    } catch (error) {
      showToast('Failed to add files to queue: ' + error, 'error');
      console.error('Queue error:', error);
    }
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="flex flex-col gap-3">
      {/* File Selection */}
      <div>
        {/* Not a <label>: the control below is a file-picker button, not a form field */}
        <span className="block text-sm font-medium text-gray-300 mb-1">
                    Audio Files
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="bg-blue-600 text-white font-medium px-3 py-2 text-sm rounded hover:bg-blue-500 transition-colors cursor-pointer"
            onClick={handleChooseFiles}
          >
                        📂 Select Files
          </button>
          <span className="text-gray-400 text-sm">
            {selectedFiles.length === 0
              ? 'No files selected'
              : `${selectedFiles.length} file(s) selected`}
          </span>
        </div>
      </div>

      {/* Selected Files List */}
      {selectedFiles.length > 0 && (
        <div className="bg-gray-700 rounded border border-gray-600 p-3 max-h-64 overflow-y-auto">
          <h4 className="text-xs font-semibold text-gray-300 mb-2">Selected Files:</h4>
          <ul className="space-y-1">
            {selectedFiles.map((file, index) => (
              <li key={index} className="text-sm text-gray-200 flex items-center justify-between gap-2 group">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <span className="text-blue-400">🎵</span>
                  <span className="truncate">{file.name}</span>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemoveFile(index)}
                  className="text-red-400 hover:text-red-300 px-2 py-1 rounded hover:bg-red-900/20"
                  title="Remove file"
                >
                                    ❌
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-amber-900/20 border border-amber-600/30 rounded-lg p-3">
        <div className="flex items-start gap-2">
          <span className="text-amber-400 text-lg">⚠️</span>
          <div className="flex-1">
            <p className="text-sm text-amber-200">
              The batch queue has not been rebuilt for the web yet — this button will fail. Single
              transcriptions do work: upload a file on the{' '}
              <button
                type="button"
                onClick={() => navigate(projectId ? `/projects/${projectId}/workspace` : '/workspace')}
                className="underline hover:text-white transition-colors cursor-pointer"
              >
                Transcribe page
              </button>
              {' '}and start a job against it. One upload can be transcribed as many times as you like.
            </p>
          </div>
        </div>
      </div>

      {/* Add to Queue Button */}
      <button
        type="button"
        onClick={handleAddToQueue}
        disabled={selectedFiles.length === 0}
        className={'w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold px-4 py-2.5 text-sm rounded '
          + 'hover:from-blue-500 hover:to-indigo-500 transition-all shadow disabled:opacity-50 disabled:cursor-not-allowed '
          + 'disabled:from-gray-600 disabled:to-gray-600 mt-2 cursor-pointer'}
      >
                📋 Add {selectedFiles.length > 0 ? `${selectedFiles.length} file(s)` : 'Files'} to Queue
      </button>
    </div>
  );
}

export default TranscribeBatchContainer;
